import { SITE_INFO, NAV_LINKS, INVOLVEMENT_CONTENT } from '../data/content'

function GetInvolvedPage() {
    return (
        <div className="pt-28 bg-[#0a192f] text-white min-h-screen transition-colors duration-500">
            {/* Hero Section */}
            <section className="relative px-4 py-20 overflow-hidden text-center">
                <div className="max-w-4xl mx-auto relative z-10">
                    <h1 className="text-5xl md:text-6xl font-heading font-bold text-white mb-6">
                        GET INVOLVED
                    </h1>
                    <p className="text-xl text-blue-100/80 leading-relaxed max-w-2xl mx-auto">
                        Join us in making computer science education accessible to all students
                    </p>
                </div>

                {/* Decorative elements */}
                <div className="absolute top-1/4 left-10 hidden lg:block opacity-20">
                    <div className="w-12 h-12 rounded-full border-2 border-white"></div>
                </div>
                <div className="absolute bottom-1/4 right-10 hidden lg:block opacity-20">
                    <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                    </svg>
                </div>
            </section>

            {/* How to get involved */}
            <section className="py-20 px-6 max-w-[1400px] mx-auto">
                <h2 className="text-3xl md:text-4xl font-heading font-bold text-blue-200 text-center mb-16">
                    HOW CAN I GET INVOLVED?
                </h2>

                <div className="grid md:grid-cols-2 gap-12">
                    {/* UCSD Students */}
                    <div className="bg-white/5 backdrop-blur-md p-8 rounded-2xl border border-white/10 flex flex-col items-center text-center hover:bg-white/10 transition-all">
                        <div className="w-full h-64 bg-white/5 rounded-xl mb-8 flex items-center justify-center overflow-hidden">
                            <img
                                src="/images/ClubPhoto5.png"
                                alt="UCSD Students"
                                className="w-full h-full object-cover opacity-80"
                            />
                        </div>
                        <h3 className="text-2xl font-heading font-bold mb-4 text-white">FOR UCSD STUDENTS</h3>
                        <p className="text-blue-100/70 mb-8 flex-grow">
                            If you are a current UCSD student and you'd like to be involved, join our Discord for the most updated information! We also hold general body meetings (GBMs) every quarter where you can learn about what we're currently doing!
                        </p>
                        <a
                            href={SITE_INFO.discordUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn bg-white text-[#0a192f] hover:bg-blue-50 w-full md:w-auto px-10 rounded-xl"
                        >
                            JOIN OUR DISCORD
                        </a>
                    </div>

                    {/* Educators */}
                    <div className="bg-white/5 backdrop-blur-md p-8 rounded-2xl border border-white/10 flex flex-col items-center text-center hover:bg-white/10 transition-all">
                        <div className="w-full h-64 bg-white/5 rounded-xl mb-8 flex items-center justify-center overflow-hidden">
                            <img
                                src="/images/Curriculum5.png"
                                alt="Educators"
                                className="w-full h-full object-cover opacity-80"
                            />
                        </div>
                        <h3 className="text-2xl font-heading font-bold mb-4 text-white">FOR EDUCATORS</h3>
                        <p className="text-blue-100/70 mb-8 flex-grow">
                            If you are an educator and you'd like to be involved with our organization, email us at{' '}
                            <a href={`mailto:${SITE_INFO.email}`} className="text-blue-300 font-semibold hover:underline">
                                {SITE_INFO.email}
                            </a>{' '}
                            or fill out our contact form and we will follow up with you!
                        </p>
                        <a
                            href={INVOLVEMENT_CONTENT.contactFormUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn bg-white text-[#0a192f] hover:bg-blue-50 w-full md:w-auto px-10 rounded-xl"
                        >
                            CONTACT FORM
                        </a>
                    </div>
                </div>
            </section>

            {/* Get in Touch Section */}
            <section className="py-32 px-6 relative overflow-hidden bg-white/2">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-24 items-center">
                        {/* Contact Info */}
                        <div>
                            <h2 className="text-5xl md:text-6xl font-heading font-bold text-white mb-8 leading-tight">GET IN TOUCH</h2>
                            <p className="text-2xl text-blue-100/70 leading-relaxed mb-16">
                                Have questions or want to get involved? Send us a message and we'll get back to you soon!
                            </p>

                            <div className="space-y-12">
                                <div className="flex gap-8 items-center">
                                    <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center text-blue-300 shrink-0">
                                        <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                            <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h4 className="text-xl font-bold text-white mb-1">Email</h4>
                                        <a href={`mailto:${SITE_INFO.email}`} className="text-blue-300 hover:underline text-2xl font-semibold">{SITE_INFO.email}</a>
                                    </div>
                                </div>

                                <div className="flex gap-8 items-center">
                                    <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center text-blue-300 shrink-0">
                                        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M20.317 4.37a19.791 19.791 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037 19.736 19.736 0 00-4.885 1.515.069.069 0 00-.032.027C.533 9.048-.32 13.572.099 18.057a.082.082 0 00.031.057 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028 14.09 14.09 0 001.226-1.994.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128c.125-.094.252-.192.37-.294a.074.074 0 01.077-.01 13.525 13.525 0 0012.245 0 .075.075 0 01.077.01c.118.102.245.2.37.294a.077.077 0 01-.009.128 12.98 12.98 0 01-1.872.892.077.077 0 00-.041.106c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" /></svg>
                                    </div>
                                    <div>
                                        <h4 className="text-xl font-bold text-white mb-1">Discord</h4>
                                        <a href={SITE_INFO.discordUrl} target="_blank" rel="noopener noreferrer" className="text-blue-300 hover:underline text-2xl font-semibold">Join our community</a>
                                    </div>
                                </div>

                                <div className="flex gap-8 items-center">
                                    <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center text-blue-300 shrink-0">
                                        <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                            <path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                    </div>
                                    <div>
                                        <h4 className="text-xl font-bold text-white mb-1">Location</h4>
                                        <p className="text-blue-100/70 text-2xl">UC San Diego, La Jolla</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Form */}
                        <div className="bg-white/5 backdrop-blur-xl p-12 rounded-[40px] border border-white/20 shadow-2xl">
                            <h3 className="text-3xl font-heading font-bold text-white mb-10">Send Us a Message</h3>

                            <form className="space-y-8">
                                <div>
                                    <label className="block text-sm font-bold text-blue-200 mb-3 uppercase tracking-widest">I'm interested in: *</label>
                                    <select className="w-full px-6 py-5 rounded-2xl border border-white/10 bg-white/5 text-white focus:border-blue-400 outline-none transition-all appearance-none text-lg">
                                        <option className="bg-[#0a192f]">General Inquiry</option>
                                        <option className="bg-[#0a192f]">Joining as a Student</option>
                                        <option className="bg-[#0a192f]">Volunteering</option>
                                        <option className="bg-[#0a192f]">Becoming a Mentor</option>
                                        <option className="bg-[#0a192f]">School Partnership</option>
                                        <option className="bg-[#0a192f]">Sponsorship</option>
                                    </select>
                                </div>

                                <div className="grid md:grid-cols-2 gap-8">
                                    <div>
                                        <label className="block text-sm font-bold text-blue-200 mb-3 uppercase tracking-widest">Name *</label>
                                        <input type="text" className="w-full px-6 py-5 rounded-2xl border border-white/10 bg-white/5 text-white focus:border-blue-400 outline-none transition-all text-lg" required placeholder="Your Name" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-blue-200 mb-3 uppercase tracking-widest">Email *</label>
                                        <input type="email" className="w-full px-6 py-5 rounded-2xl border border-white/10 bg-white/5 text-white focus:border-blue-400 outline-none transition-all text-lg" required placeholder="Your Email" />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-bold text-blue-200 mb-3 uppercase tracking-widest">Message *</label>
                                    <textarea className="w-full px-6 py-5 rounded-2xl border border-white/10 bg-white/5 text-white focus:border-blue-400 outline-none transition-all h-40 resize-none text-lg" required placeholder="How can we help?"></textarea>
                                </div>

                                <button type="submit" className="btn bg-white text-[#0a192f] hover:bg-blue-50 w-full rounded-2xl py-6 shadow-xl active:scale-[0.98] transition-all font-bold text-xl uppercase tracking-wider">
                                    SEND MESSAGE
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="py-24 px-6 bg-[#0c1e35]">
                <div className="max-w-4xl mx-auto">
                    <h2 className="text-4xl md:text-5xl font-heading font-bold text-blue-200 text-center mb-20">
                        FREQUENTLY ASKED QUESTIONS
                    </h2>

                    <div className="space-y-12">
                        {INVOLVEMENT_CONTENT.faq.map((item, index) => (
                            <div key={index} className="border-b border-white/5 pb-10">
                                <h3 className="text-2xl font-heading font-bold text-white mb-4">
                                    {item.q}
                                </h3>
                                <p className="text-blue-100/60 leading-relaxed text-xl">
                                    {item.a}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    )
}

export default GetInvolvedPage
